import React, { memo, useEffect } from 'react';
// Hooks
import { AdjustableComponent } from '../../../../../utils/hooks/useClasses';
import { useForm } from 'plume-web-utils';
// Component
import {
  Button,
  InputField,
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
  notify,
} from 'plume-ui';
import { activePartnerAtom, urlModalAtom } from '../../../../../state';
import { useRecoilValue, useResetRecoilState } from 'recoil';
import { ModalStyles } from 'plume-ui/dist/components/Modal/Modal';
import FormattedMessage from '../../../../../utils/components/FormattedMessage';
import { useTranslation } from 'react-i18next';
import { DependencyContainer } from '../../../../../DependencyContainer';
import { testUrlRegex } from '../fixtures';
import { AxiosError } from 'axios';
import { useTrackEvent } from '../../../../trackingAnalytics/hooks/useTrackEvent';
import { MixPanelEvents } from '../../../../../mixPanelEvents';

type UrlModalProps = {};
type UrlModalStyles = {};
type UrlFormValues = {
  urlLink: string;
};

const { outreachService } = new DependencyContainer();

const UrlModal: AdjustableComponent<UrlModalProps, UrlModalStyles> = () => {
  const { t } = useTranslation();
  const trackEvent = useTrackEvent();
  const modalProps = useRecoilValue(urlModalAtom);
  const resetModal = useResetRecoilState(urlModalAtom);
  const activePartner = useRecoilValue(activePartnerAtom);
  const [saving, setSaving] = React.useState(false);

  const form = useForm<UrlFormValues>({
    initialValues: {
      urlLink: modalProps.url.urlLink || '',
    },
    validationSchema: {
      urlLink: {
        required: true,
        validator: (value: string) => testUrlRegex.test(value),
      },
    },
    validateOnChange: true,
  });

  const handleFinishError = (message?: string) => {
    notify({
      title: t(
        `configuration.notifications.error.url.${modalProps.type}.title`,
      ),
      body: message
        ? message
        : t(`configuration.notifications.error.url.${modalProps.type}.body`),
      type: 'error',
    });
    trackEvent({
      eventName: MixPanelEvents.URL_FAILURE,
      additionalContent: {
        INTERACTION: modalProps.type.toUpperCase(),
        URL_ID: modalProps.type === 'edit' ? modalProps.url.id : undefined,
        ERROR_MESSAGE: message || 'no message',
      },
    });
    setSaving(false);
  };

  const handleFinishClick = async () => {
    if (saving || !form.validateForm()) {
      return;
    }

    setSaving(true);

    if (!activePartner?.id) {
      handleFinishError();
      return;
    }

    try {
      if (modalProps.type === 'edit' && modalProps.url.id) {
        await outreachService.updateCTAUrl(activePartner.id, {
          ...modalProps.url,
          urlLink: form.values.urlLink,
        });
      } else {
        await outreachService.saveCTAUrl(activePartner.id, form.values.urlLink);
      }

      notify({
        title: t(
          `configuration.notifications.success.url.${modalProps.type}.title`,
        ),
        body: t(
          `configuration.notifications.success.url.${modalProps.type}.body`,
        ),
        type: 'success',
      });

      trackEvent({
        eventName: MixPanelEvents.URL_SUCCESS,
        additionalContent: {
          INTERACTION: modalProps.type.toUpperCase(),
          URL_ID: modalProps.type === 'edit' ? modalProps.url.id : undefined,
        },
      });

      modalProps.refetchUrls();
      setSaving(false);
      resetModal();
    } catch (error) {
      handleFinishError(
        (error as AxiosError<{ message: string }>).response?.data.message,
      );
    }
  };

  const handleTestUrl = () => {
    const url = form.values.urlLink;
    window.open(url.includes('http') ? url : `https://${url}`);
  };

  useEffect(() => {
    form.setValues({
      urlLink: modalProps.url.urlLink || '',
    });
  }, [modalProps]);

  return (
    <Modal
      classes={(current: ModalStyles) => ({
        root: `${current.root} Configuration__modal Configuration__modal--url-email`,
        title: ``,
      })}
      isOpen={modalProps.isOpen}
      onRequestClose={resetModal}
    >
      <ModalHeader
        title={
          modalProps.type === 'add'
            ? t('configuration.modals.url.add')
            : t('configuration.modals.url.edit')
        }
      />
      <ModalBody>
        <div className="Configuration__modal__inputs-container">
          <InputField
            classes={(current) => ({
              ...current,
              root: `${current.root} ${
                form.values.urlLink === '' ? 'InputField--empty' : ''
              }`,
            })}
            label={t('configuration.modals.url.url')}
            id="input-urlLink"
            required
            requiredAsterix
            value={form.values.urlLink}
            onChange={(event) =>
              form.handleChange('urlLink', event.target.value)
            }
            onBlur={() => form.handleBlur('urlLink')}
          />

          <div className="Configuration__modal__test">
            <Button
              styleVariant="tertiary"
              onClick={handleTestUrl}
              disabled={!form.isValid}
            >
              <FormattedMessage id="configuration.modals.url.test" />
            </Button>
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button styleVariant="tertiary-grey" onClick={resetModal}>
          <FormattedMessage id="confirmation.cancel" />
        </Button>
        <Button
          styleVariant="superprimary"
          onClick={handleFinishClick}
          disabled={!form.isValid || saving}
        >
          <FormattedMessage id="confirmation.finish" />
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default memo(UrlModal);
